import React, { useState } from 'react';

export default function StatusReportPopup({ countries, currentTurn, onClose }) {
  const [currentPage, setCurrentPage] = useState(0);
  
  const handleClose = () => {
    console.log('[STATUS REPORT] Close triggered');
    onClose();
  };
  
  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, 3));
  };
  
  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };
  
  const pages = ['Overview', 'Resources', 'Military', 'Diplomacy'];

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          console.log('Clicked backdrop - closing status report');
          handleClose();
        }
      }}
    >
      <div 
        className="bg-gradient-to-br from-gray-900 to-gray-800 border-4 border-yellow-500 rounded-lg p-8 max-w-6xl w-full mx-4 relative max-h-[90vh] overflow-hidden"
        style={{
          boxShadow: '0 0 40px rgba(234, 179, 8, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-red-600 hover:bg-red-500 transition-all z-[70] pointer-events-auto"
          style={{ fontSize: '24px', lineHeight: '1', cursor: 'pointer' }}
          title="Close"
        >
          ×
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 
            className="text-4xl font-black uppercase tracking-wider mb-2"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              textShadow: '0 0 20px rgba(234, 179, 8, 0.8)',
              color: '#fbbf24'
            }}
          >
            📊 ROUND {Math.max(1, Math.ceil((currentTurn || 1) / 5))} STATUS REPORT
          </h2>
          <p className="text-gray-400 text-sm">Turn {currentTurn || 1} - Global Intelligence Summary</p>
          
          {/* Page Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {pages.map((page, idx) => (
              <button
                key={page}
                onClick={() => setCurrentPage(idx)}
                className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                  currentPage === idx
                    ? 'bg-yellow-500 text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        </div>

        {/* Content Container with overflow */}
        <div className="overflow-y-auto max-h-[calc(90vh-250px)] pr-2">
          {/* PAGE 0: OVERVIEW */}
          {currentPage === 0 && (
            <div className="space-y-3">
              {countries.map((country) => {
                const enrichmentPercent = country.resources.uranium_enrichment || 0;
                const isNuclearReady = enrichmentPercent >= 100;
                const nukeCount = country.resources.nukes_ready || 0;

                return (
                  <div 
                    key={country.code}
                    className="bg-black/40 border-2 rounded-lg p-4 hover:bg-black/60 transition-all"
                    style={{ borderColor: country.is_player ? '#3b82f6' : '#6b7280' }}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      {/* Country Name */}
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: country.is_player ? '#3b82f6' : '#6b7280' }}
                        />
                        <span 
                          className="text-xl font-bold uppercase"
                          style={{
                            fontFamily: "'Orbitron', sans-serif",
                            color: country.is_player ? '#3b82f6' : '#fff'
                          }}
                        >
                          {country.name}
                        </span>
                        {country.is_player && (
                          <span className="text-xs bg-blue-500 px-2 py-1 rounded">YOU</span>
                        )}
                      </div>

                      {/* Stats Grid */}
                      <div className="flex items-center gap-6 flex-wrap">
                        {/* Population */}
                        <div className="text-center">
                          <div className="text-xs text-gray-400 uppercase mb-1">Population</div>
                          <div className="text-lg font-bold text-green-400">
                            {((country.resources.population || 0) / 1000000).toFixed(1)}M
                          </div>
                        </div>

                        {/* Enrichment */}
                        <div className="text-center min-w-[200px]">
                          <div className="text-xs text-gray-400 uppercase mb-1">Enrichment</div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-700 rounded-full h-4 overflow-hidden">
                              <div 
                                className="h-full transition-all duration-500"
                                style={{
                                  width: `${Math.min(enrichmentPercent, 100)}%`,
                                  background: isNuclearReady 
                                    ? 'linear-gradient(to right, #dc2626, #ef4444)' 
                                    : 'linear-gradient(to right, #f59e0b, #fbbf24)'
                                }}
                              />
                            </div>
                            <span 
                              className="text-lg font-bold min-w-[60px]"
                              style={{ color: isNuclearReady ? '#ef4444' : '#fbbf24' }}
                            >
                              {(enrichmentPercent || 0).toFixed(0)}%
                            </span>
                          </div>
                        </div>

                        {/* Nukes */}
                        <div className="text-center min-w-[80px]">
                          <div className="text-xs text-gray-400 uppercase mb-1">Nukes</div>
                          <div 
                            className="text-2xl font-bold"
                            style={{ color: nukeCount > 0 ? '#ef4444' : '#6b7280' }}
                          >
                            {nukeCount > 0 ? `💣 ${nukeCount}` : '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* PAGE 1: RESOURCES */}
          {currentPage === 1 && (
            <div className="space-y-3">
              {countries.map((country) => (
                <div 
                  key={country.code}
                  className="bg-black/40 border-2 rounded-lg p-4"
                  style={{ borderColor: country.is_player ? '#3b82f6' : '#6b7280' }}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <span 
                        className="text-xl font-bold uppercase"
                        style={{
                          fontFamily: "'Orbitron', sans-serif",
                          color: country.is_player ? '#3b82f6' : '#fff'
                        }}
                      >
                        {country.name}
                      </span>
                      {country.is_player && <span className="text-xs bg-blue-500 px-2 py-1 rounded">YOU</span>}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center bg-black/30 p-3 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-1">💰 Funds</div>
                      <div className="text-2xl font-bold text-yellow-400">
                        ${country.resources.money || 0}
                      </div>
                    </div>
                    <div className="text-center bg-black/30 p-3 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-1">📢 Influence</div>
                      <div className="text-2xl font-bold text-purple-400">
                        {country.resources.influence || 0}
                      </div>
                    </div>
                    <div className="text-center bg-black/30 p-3 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-1">👥 Population</div>
                      <div className="text-2xl font-bold text-green-400">
                        {((country.resources.population || 0) / 1000000).toFixed(1)}M
                      </div>
                    </div>
                    <div className="text-center bg-black/30 p-3 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-1">⚛️ Enrichment</div>
                      <div className="text-2xl font-bold text-orange-400">
                        {(country.resources.uranium_enrichment || 0).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* PAGE 2: MILITARY */}
          {currentPage === 2 && (
            <div className="space-y-3">
              {countries.map((country) => (
                <div 
                  key={country.code}
                  className="bg-black/40 border-2 rounded-lg p-4"
                  style={{ borderColor: country.is_player ? '#3b82f6' : '#6b7280' }}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <span 
                        className="text-xl font-bold uppercase"
                        style={{
                          fontFamily: "'Orbitron', sans-serif",
                          color: country.is_player ? '#3b82f6' : '#fff'
                        }}
                      >
                        {country.name}
                      </span>
                      {country.is_player && <span className="text-xs bg-blue-500 px-2 py-1 rounded">YOU</span>}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center bg-black/30 p-4 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-2">⚔️ Military Power</div>
                      <div className="text-3xl font-bold text-red-400">
                        {country.resources.military || 0}
                      </div>
                    </div>
                    <div className="text-center bg-black/30 p-4 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-2">☢️ Enrichment Progress</div>
                      <div className="w-full bg-gray-700 rounded-full h-6 overflow-hidden mb-2">
                        <div 
                          className="h-full transition-all"
                          style={{
                            width: `${Math.min(country.resources.uranium_enrichment || 0, 100)}%`,
                            background: (country.resources.uranium_enrichment || 0) >= 100
                              ? 'linear-gradient(to right, #dc2626, #ef4444)'
                              : 'linear-gradient(to right, #f59e0b, #fbbf24)'
                          }}
                        />
                      </div>
                      <div className="text-xl font-bold text-orange-400">
                        {(country.resources.uranium_enrichment || 0).toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-center bg-black/30 p-4 rounded">
                      <div className="text-xs text-gray-400 uppercase mb-2">💣 Nuclear Arsenal</div>
                      <div className="text-3xl font-bold text-red-500">
                        {(country.resources.nukes_ready || 0) > 0 ? (
                          `${country.resources.nukes_ready} 💣`
                        ) : (
                          <span className="text-gray-500">None</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* PAGE 3: DIPLOMACY */}
          {currentPage === 3 && (
            <div className="space-y-3">
              {countries.map((country) => {
                const relations = country.relations || {};
                const relationKeys = Object.keys(relations);
                
                return (
                  <div 
                    key={country.code}
                    className="bg-black/40 border-2 rounded-lg p-4"
                    style={{ borderColor: country.is_player ? '#3b82f6' : '#6b7280' }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span 
                        className="text-xl font-bold uppercase"
                        style={{
                          fontFamily: "'Orbitron', sans-serif",
                          color: country.is_player ? '#3b82f6' : '#fff'
                        }}
                      >
                        {country.name}
                      </span>
                      {country.is_player && <span className="text-xs bg-blue-500 px-2 py-1 rounded">YOU</span>}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {relationKeys.map((targetCode) => {
                        const targetCountry = countries.find(c => c.code === targetCode);
                        const relationValue = relations[targetCode];
                        const relationColor = relationValue >= 50 ? '#10b981' : relationValue >= 0 ? '#f59e0b' : '#ef4444';
                        const relationLabel = relationValue >= 50 ? '🤝 Allied' : relationValue >= 0 ? '😐 Neutral' : '⚔️ Hostile';
                        
                        return (
                          <div key={targetCode} className="bg-black/30 p-3 rounded text-center">
                            <div className="text-xs text-gray-400 uppercase mb-1">
                              vs {targetCountry?.name || targetCode}
                            </div>
                            <div 
                              className="text-lg font-bold mb-1"
                              style={{ color: relationColor }}
                            >
                              {relationValue >= 0 ? '+' : ''}{relationValue}
                            </div>
                            <div className="text-xs" style={{ color: relationColor }}>
                              {relationLabel}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Navigation Arrows + Close Button */}
        <div className="mt-6 flex items-center justify-between gap-4">
          {/* Left Arrow */}
          <button
            onClick={prevPage}
            disabled={currentPage === 0}
            className={`px-4 py-3 font-bold text-lg rounded-lg transition-all ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500 text-white hover:scale-105'
            }`}
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            ← Previous
          </button>

          {/* Continue Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Continue button clicked - closing status report');
              handleClose();
            }}
            className="px-8 py-3 text-lg font-bold uppercase tracking-wider rounded-lg transition-all hover:scale-105 active:scale-95 z-[70] pointer-events-auto"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
              color: '#ffffff',
              cursor: 'pointer',
              position: 'relative'
            }}
          >
            Continue Game
          </button>

          {/* Right Arrow */}
          <button
            onClick={nextPage}
            disabled={currentPage === 3}
            className={`px-4 py-3 font-bold text-lg rounded-lg transition-all ${
              currentPage === 3
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500 text-white hover:scale-105'
            }`}
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Next →
          </button>
        </div>

        {/* Close Button */}
        <div className="mt-6 text-center" style={{ display: 'none' }}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Continue button clicked - closing status report');
              handleClose();
            }}
            className="px-8 py-3 text-lg font-bold uppercase tracking-wider rounded-lg transition-all hover:scale-105 active:scale-95 z-[70]"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
              color: '#ffffff',
              cursor: 'pointer',
              position: 'relative'
            }}
          >
            Continue Game
          </button>
        </div>
      </div>
    </div>
  );
}
